import React from 'react'
import { LinkVariant } from '../../types/LinkVariant.type'
import { LinkTarget } from '../../types/LinkTarget.type'
import { LinkSize } from './LinkSize.type'
import xss from 'xss'

export type LinkProps = {
  name: string
  url: string
  target?: LinkTarget | ''
  analyticsName?: string
  size?: LinkSize
  variant?: LinkVariant
  color?: string
  testId?: string
  className?: string
  ariaLabel?: string
} & React.AnchorHTMLAttributes<HTMLAnchorElement>

/**
 * Links are used to navigate to another page, anchor, or resource. They may be styled to look like buttons,
 * but unlike buttons, they do not feature onClick handlers for actions. Instead, they use the `href` attribute
 * to define the navigation target. Use links for navigation and buttons for actions that do not change the URL.
 */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (props: LinkProps, ref) => {
    const {
      name,
      url,
      target,
      analyticsName,
      size,
      variant,
      testId,
      className,
      ariaLabel,
      ...rest
    } = props

    const dataAnalytics = analyticsName
      ? { 'data-analytics': analyticsName }
      : {}

    const normalizeUrl = (linkUrl: string): string => {
      const xssUrl = xss(linkUrl)
      return xssUrl.replace(/&amp;/g, '&')
    }

    const classNames = []
    switch (variant) {
      case LinkVariant.PRIMARY:
        classNames.push('Button', 'Button--primary')
        break
      case LinkVariant.SECONDARY:
        classNames.push('Button', 'Button--secondary')
        classNames.push('Button', 'Button--secondary--action-blue')
        break
      case LinkVariant.TERTIARY:
        classNames.push('Button', 'Button--tertiary')
        break
      default:
        classNames.push('Link')
    }
    if (size === 'small') {
      classNames.push('Button--small')
    }
    if (className) {
      classNames.push(className)
    }

    return (
      <a
        ref={ref}
        href={normalizeUrl(url)}
        target={target === '' ? undefined : target}
        aria-label={ariaLabel}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        className={classNames.join(' ')}
        data-testid={testId}
        {...dataAnalytics}
        {...rest}
        dangerouslySetInnerHTML={{ __html: xss(name) }}
      />
    )
  }
)
